import React from 'react';
import Icon from '../../../components/AppIcon';

const ContentSuggestionsModal = ({ contentSuggestions, onClose, loading }) => {
  if (!contentSuggestions && !loading) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-600 to-teal-600 text-white p-6">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-2xl font-bold mb-2 flex items-center gap-2">
                <Icon name="FileText" size={24} />
                AI Content Improvement Suggestions
              </h2>
              <p className="text-green-100">Optimize your invoice emails for better deliverability</p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-colors"
            >
              <Icon name="X" size={24} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mb-4"></div>
              <p className="text-neutral-600">Analyzing email content with AI...</p>
            </div>
          ) : contentSuggestions ? (
            <div className="space-y-6">
              {/* Overall Score */}
              {contentSuggestions?.overallScore && (
                <div className="bg-gradient-to-r from-green-50 to-teal-50 rounded-lg border border-green-200 p-6">
                  <h3 className="font-semibold text-neutral-900 mb-4 flex items-center gap-2">
                    <Icon name="TrendingUp" size={20} />
                    Deliverability Score
                  </h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center">
                      <p className="text-sm text-neutral-600 mb-1">Before</p>
                      <p className="text-3xl font-bold text-red-600">{contentSuggestions?.overallScore?.before}/100</p>
                    </div>
                    <div className="flex items-center justify-center">
                      <Icon name="ArrowRight" size={32} className="text-neutral-400" />
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-neutral-600 mb-1">After</p>
                      <p className="text-3xl font-bold text-green-600">{contentSuggestions?.overallScore?.after}/100</p>
                    </div>
                  </div>
                  <p className="text-center mt-4 text-sm text-neutral-700">
                    {contentSuggestions?.overallScore?.improvement}
                  </p>
                </div>
              )}

              {/* Subject Line Improvements */}
              {contentSuggestions?.subjectLineImprovements && (
                <div className="bg-white rounded-lg border border-neutral-200 p-4">
                  <h3 className="font-semibold text-neutral-900 mb-3 flex items-center gap-2">
                    <Icon name="Mail" size={18} className="text-blue-600" />
                    Subject Line Optimization
                  </h3>
                  <div className="space-y-3">
                    {contentSuggestions?.subjectLineImprovements?.original && (
                      <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                        <p className="text-xs text-neutral-600 mb-1">Original:</p>
                        <p className="text-sm text-neutral-900">{contentSuggestions?.subjectLineImprovements?.original}</p>
                      </div>
                    )}
                    <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                      <p className="text-xs text-neutral-600 mb-1">Suggested:</p>
                      <p className="text-sm font-medium text-neutral-900">{contentSuggestions?.subjectLineImprovements?.suggested}</p>
                    </div>
                    <div className="p-3 bg-blue-50 rounded-lg">
                      <p className="text-xs text-neutral-600 mb-1">Reasoning:</p>
                      <p className="text-sm text-neutral-700">{contentSuggestions?.subjectLineImprovements?.reasoning}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Body Improvements */}
              {contentSuggestions?.bodyImprovements && contentSuggestions?.bodyImprovements?.length > 0 && (
                <div className="bg-white rounded-lg border border-neutral-200 p-4">
                  <h3 className="font-semibold text-neutral-900 mb-3 flex items-center gap-2">
                    <Icon name="FileText" size={18} className="text-purple-600" />
                    Email Body Improvements
                  </h3>
                  <div className="space-y-3">
                    {contentSuggestions?.bodyImprovements?.map((improvement, index) => (
                      <div key={index} className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                        <div className="flex items-start gap-2 mb-2">
                          <Icon name="AlertCircle" size={16} className="text-purple-600 mt-1" />
                          <div className="flex-1">
                            <p className="text-sm font-medium text-neutral-900 mb-1">{improvement?.issue}</p>
                            <p className="text-sm text-neutral-700 mb-2">{improvement?.suggestion}</p>
                            <p className="text-xs text-purple-700 bg-purple-100 px-2 py-1 rounded inline-block">
                              Impact: {improvement?.impact}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Spam Triggers */}
              {contentSuggestions?.spamTriggers && contentSuggestions?.spamTriggers?.length > 0 && (
                <div className="bg-white rounded-lg border border-neutral-200 p-4">
                  <h3 className="font-semibold text-neutral-900 mb-3 flex items-center gap-2">
                    <Icon name="AlertTriangle" size={18} className="text-orange-600" />
                    Spam Trigger Words Detected
                  </h3>
                  <div className="space-y-2">
                    {contentSuggestions?.spamTriggers?.map((trigger, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-orange-50 rounded-lg border border-orange-200">
                        <div className="flex items-center gap-2">
                          <Icon name="X" size={16} className="text-red-600" />
                          <span className="text-sm text-neutral-900 line-through">{trigger?.trigger}</span>
                        </div>
                        <Icon name="ArrowRight" size={16} className="text-neutral-400" />
                        <div className="flex items-center gap-2">
                          <Icon name="Check" size={16} className="text-green-600" />
                          <span className="text-sm font-medium text-neutral-900">{trigger?.replacement}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-12">
              <Icon name="AlertCircle" size={48} className="mx-auto text-neutral-300 mb-4" />
              <p className="text-neutral-600">No content suggestions available</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-neutral-50 px-6 py-4 border-t border-neutral-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-neutral-200 text-neutral-700 rounded-lg hover:bg-neutral-300 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ContentSuggestionsModal;
